import React, { useEffect, useState } from "react";
import MailConnectPanel from "./MailConnectPanel";
import PurgePanel from "./PurgePanel";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["podio_webhook", "Podio Webhook URL"],
  ["make_webhook", "Make.com Webhook URL"],
  ["zapier_webhook", "Zapier Webhook URL"],
  ["instagram_url", "Instagram URL"],
  ["google_analytics_id", "Google Analytics ID"],
  ["meta_pixel_id", "Meta Pixel ID"],
  ["notification_emails", "Admin notification emails (comma separated)"],
  ["sender_name", "Sender name (Gmail/SendGrid From)"],
  ["admin_phone", "Admin SMS recipient (E.164, e.g. +19166780058)"],
];

export default function SettingsPanel() {
  const [s, setS] = useState({});

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data || {})).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.patch("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="min-h-screen bg-black text-neutral-200">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#C19B6C]"><ArrowLeft size={14} /> Back</Link>
          <button onClick={save} className="btn-gold px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="settings-save"><Save size={14}/> Save</button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display text-3xl">Integrations & Settings</h1>
        <p className="text-sm text-neutral-400 mt-3 max-w-xl">
          When a form is submitted, the system fans out the structured payload to every configured webhook in parallel,
          and sends owner notifications through the connected Google Workspace mailbox to the recipients below.
        </p>
        <div className="mt-8 card-frame p-6 grid grid-cols-1 gap-5">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-wider text-neutral-500">{label}</label>
              <input
                value={s[k] || ""}
                onChange={(e) => setS({ ...s, [k]: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm"
                data-testid={`setting-${k}`}
                placeholder={k.includes("webhook") ? "https://…" : ""}
              />
            </div>
          ))}
          <label className="flex items-start gap-3 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={!!s.sms_enabled}
              onChange={(e) => setS({ ...s, sms_enabled: e.target.checked })}
              className="mt-1 accent-[#C19B6C]"
              data-testid="setting-sms-enabled"
            />
            <span>
              <span className="text-sm text-neutral-200">Enable Hot-seller SMS alerts</span>
              <span className="block text-xs text-neutral-500 mt-1">
                Texts the Admin SMS recipient above whenever a Seller lead scores Hot AND Intent = "Ready to move forward".
                Requires <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_FROM_NUMBER</code> in backend env.
              </span>
            </span>
          </label>
        </div>
        <div className="mt-6 text-xs text-neutral-500">
          SendGrid API key + verified sender are configured via backend env (<code>SENDGRID_API_KEY</code>, <code>SENDGRID_FROM_EMAIL</code>).
          Twilio credentials are configured via backend env (<code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_FROM_NUMBER</code>).
        </div>
        <MailConnectPanel />
        <PurgePanel />
      </div>
    </div>
  );
}
